import React, { FormEvent, useState } from "react";
import { useHistory } from "react-router-dom";
import graphql from "babel-plugin-relay/macro";
import { useMutation } from "react-relay/hooks";

import {
  createUserInput,
  CreateUserMutation,
} from "../__generated__/CreateUserMutation.graphql";
import type { CreateLoginResponse } from "../_types";
import { isApiError } from "../_types/errorTypes";

import * as Yup from "yup";
import { HiddenSubmitButton, LoadingSpinnerComplete } from "../_components";
import { AppContext } from "../_context/appContext";
import { UniqueUserContext } from "../_context/uniqueUserContext";
import { AuthActionTypes } from "../_reducers";
import { ModalError } from "../_components/ModalError";
import Button from "../_components/Button";
import { TextInputIcon } from "../_components/TextInputIcon";
import {
  FORM_PLACEHOLDERS,
  getFieldErrors,
  YUP_FIELDS,
} from "../_utilities/forms";
import Spacer from "../_components/Spacer";
import {
  FieldError,
  InputFields,
  StyledForm,
} from "../_components/FormComponents";
import { VersionCopyright } from "../_components/VersionCopyright";

const USER_CREDENTIALS_SCHEMA = Yup.object().shape({
  email: YUP_FIELDS.email,
  password: YUP_FIELDS.password,
  ui_passwordConfirm: YUP_FIELDS.ui_passwordConfirm,
});

export function CreateUser() {
  const history = useHistory();
  const { state, dispatch } = React.useContext(AppContext);
  const { uniqueUser, updateUniqueUser } = React.useContext(UniqueUserContext);

  type LoginInput = {
    email: string;
    password: string;
    ui_passwordConfirm: string;
  };
  const LOGIN_INPUT_INITIAL = {
    email: "",
    password: "",
    ui_passwordConfirm: "",
  };
  const [inputVals, setInputVals] =
    React.useState<LoginInput>(LOGIN_INPUT_INITIAL);

  const [formInputIsInvalid, setFormInputIsInvalid] = useState<boolean>(false);

  const [errMsg, setErrMsg] = useState<string>("");

  const [commit, isInFlight] = useMutation<CreateUserMutation>(graphql`
    mutation CreateUserMutation($input: createUserInput!) {
      createUser(input: $input) {
        jwt {
          token
          expiresIn
          expiresAt
        }
        user {
          id
          email
          roles
          boxIntervals
        }
        errors {
          __typename
          ... on UserError {
            message
          }
        }
      }
    }
  `);

  function saveUserData(userData: Partial<CreateLoginResponse>) {
    if (userData && userData.jwt && userData.user) {
      dispatch({
        type: AuthActionTypes.Set,
        payload: {
          user: userData?.user,
          jwt: userData?.jwt,
        },
      });
      updateUniqueUser(userData?.user.id);
    }
  }

  function handleLogin(input: createUserInput) {
    commit({
      variables: {
        input,
      },
      onCompleted: (response) => {
        if (response.createUser) {
          const { jwt, user, errors } = response.createUser;
          if (jwt && user) {
            saveUserData({ user, jwt });
          }
          if (errors.length > 0) {
            const errMsgs = errors.map((err) => err.message);
            setErrMsg(errMsgs.join("; "));
            // setFriendlyErrors(errors);
          }
        }
      },
      onError: (err) => {
        if (isApiError(err)) {
          setErrMsg(err.message);
        }
      },
    });
  }

  function handleSubmit(e: FormEvent) {
    // make sure e.preventDefault() comes first for form submission
    e.preventDefault();
    if (isInFlight) return;

    if (!USER_CREDENTIALS_SCHEMA.isValidSync(inputVals)) {
      console.log("Input vals fail schema validation.");
      setFormInputIsInvalid(true);
      return;
    }
    const { email, password } = inputVals;
    handleLogin({ email, password });
  }

  return (
    <>
      <StyledForm onSubmit={handleSubmit}>
        <InputFields>
          <TextInputIcon
            // name="email"
            type="email"
            label="Email"
            value={inputVals.email}
            onChange={(val) =>
              setInputVals({
                ...inputVals,
                email: val,
              })
            }
            size="large"
            mode="border"
            placeholder={FORM_PLACEHOLDERS.email}
          />
          <FieldError>
            {getFieldErrors(
              USER_CREDENTIALS_SCHEMA,
              inputVals,
              "email",
              formInputIsInvalid
            )}
          </FieldError>
          <TextInputIcon
            // name="password"
            type="password"
            label="Password"
            value={inputVals.password}
            onChange={(val) =>
              setInputVals({
                ...inputVals,
                password: val,
              })
            }
            size="large"
            mode="border"
          />
          <FieldError>
            {getFieldErrors(
              USER_CREDENTIALS_SCHEMA,
              inputVals,
              "password",
              formInputIsInvalid
            )}
          </FieldError>
          <TextInputIcon
            // name="ui_passwordConfirm"
            type="password"
            label="Confirm Password"
            value={inputVals.ui_passwordConfirm}
            onChange={(val) =>
              setInputVals({
                ...inputVals,
                ui_passwordConfirm: val,
              })
            }
            size="large"
            mode="border"
          />
          <FieldError>
            {getFieldErrors(
              USER_CREDENTIALS_SCHEMA,
              inputVals,
              "ui_passwordConfirm",
              formInputIsInvalid
            )}
          </FieldError>
        </InputFields>
        <Button
          type="submit"
          // allow attempted submission when inputs are invalid, to trigger display of input errors
          disabled={isInFlight}
          palette={isInFlight ? "mono" : "primary"}
          expand="block"
        >
          Create Account
        </Button>
        {isInFlight && <LoadingSpinnerComplete overlay />}
      </StyledForm>
      <Spacer size={16} axis="vert" />
      <Button size="sm" fill="ghost" onClick={() => history.push("/intro")}>
        View Intro
      </Button>
      <Spacer size={32} axis="vert" />
      <VersionCopyright />
      <ModalError errMsg={errMsg} setErrMsg={setErrMsg} />
    </>
  );
}
